import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, Typography } from "@mui/material";
import { FC } from "react";
import { FormattedMessage } from "react-intl";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import { GenericAutocompleteUtils } from "../../../../components/GenericAutocomplete/GenericAutocompleteUtils";
import GenericAutocomplete from "../../../../components/GenericAutocomplete/GenericAutocomplete";
import store from "../../../../store";
import { getJourneyLabelsByTenantId } from "../../Campaign/components/common/utils";
import { ResponseResult } from "../../../../components/GenericAutocomplete/GenericAutocompleteTypes";
import { migrateLabelTag } from "../../../../config/axios/apis/labelTags/labelTagsAPI";
import { BO_CORE_DEFAULT_NUMBER_VALUE } from "../../../../utils/AppUtils";
import { uiActions } from "../../../../reducers/uiReducer";

interface IFormData {
    disableSourceLabel: boolean;
    targetLabel: {
        id: number;
        name: string;
        createdValue?: string;
    } | null;
}

export interface IMigratableLabel {
    id: number;
    name: string;
    usageCount: number;
    type: string;
}

interface MigrateLabelModalProps {
    labelInstance: IMigratableLabel;
    open: boolean;
    onClose: VoidFunction;
}

const MigrateLabelModal: FC<MigrateLabelModalProps> = ({ labelInstance, open, onClose }) => {
    const { control, handleSubmit, reset } = useForm<IFormData>({
        resolver: yupResolver(
            yup.object({
                disableSourceLabel: yup.boolean().default(false),
                targetLabel: yup
                    .object({ id: yup.number().defined(), name: yup.string().ensure(), createdValue: yup.string() })
                    .required()
                    .nullable()
                    .notOneOf([null], "Target label is required")
                    .default(null),
            }),
        ),
        mode: "onBlur",
        reValidateMode: "onBlur",
        criteriaMode: "all",
        defaultValues: {
            disableSourceLabel: false,
            targetLabel: null,
        },
    });
    const dispatch = useDispatch();

    const onSubmitForm = (data: IFormData) => {
        const dto = {
            disable: data.disableSourceLabel,
            targetTag: {
                id: data.targetLabel!.id !== BO_CORE_DEFAULT_NUMBER_VALUE ? data.targetLabel!.id : undefined,
                name: data.targetLabel!.id !== BO_CORE_DEFAULT_NUMBER_VALUE ? undefined : data.targetLabel!.createdValue,
            },
        };

        migrateLabelTag(labelInstance.id, dto).then(() => {
            onClose();
            reset();
            dispatch(uiActions.setForceRefreshGrid(true));
        });
    };

    return (
        <Dialog
            open={open}
            onClose={() => {
                onClose();
                reset();
            }}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                <FormattedMessage id="migrate-label-tag-modal.title" defaultMessage="Migrate Label" />
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ padding: "16px 16px 16px 16px" }}>
                <Box sx={{ marginBottom: "24px" }}>
                    <Alert sx={{ marginBottom: "8px" }} severity="info">
                        <FormattedMessage
                            id="migrate-label-tag-modal.label.sub-heading"
                            defaultMessage="Move all instances from {labelName} to another label."
                            values={{
                                labelName: <strong>{labelInstance.name}</strong>,
                            }}
                        />
                    </Alert>
                    <Typography sx={{ marginBottom: "8px" }} variant="body1">
                        <FormattedMessage id="migrate-label-tag-modal.label.migrating-from" defaultMessage="Migrating From" />
                    </Typography>
                    <Box sx={{ display: "flex", border: "1px solid black", padding: "10px 12px", borderRadius: ".5rem" }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                flexGrow: 1,
                            }}
                        >
                            <Typography
                                sx={{ overflow: "hidden", textOverflow: "ellipsis", WebkitLineClamp: "2", WebkitBoxOrient: "vertical", display: "-webkit-box" }}
                                variant="body1"
                            >
                                {labelInstance.name}
                            </Typography>
                        </Box>
                        <Chip
                            label={
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: "bold",
                                    }}
                                >
                                    <FormattedMessage
                                        id="migrate-label-tag-modal.label.uses"
                                        defaultMessage="{count} uses"
                                        values={{ count: labelInstance.usageCount }}
                                    />
                                </Typography>
                            }
                        />
                    </Box>
                </Box>

                <Box>
                    <Controller
                        control={control}
                        name="targetLabel"
                        render={({ field, fieldState: { invalid, error } }) => (
                            <GenericAutocomplete
                                id="targetLabel"
                                listboxId="targetLabel-listbox"
                                label={<FormattedMessage id="migrate-label-tag-modal.label.migrate-to" defaultMessage="Migrate To" />}
                                placeholder={"Migrating To "}
                                excludeId={labelInstance.id}
                                error={invalid}
                                errorMessage={
                                    invalid && error ? (
                                        <FormattedMessage id={`migrate-label-tag-modal.error.${error.type}`} defaultMessage={error.message} />
                                    ) : (
                                        " "
                                    )
                                }
                                options={[] as { id: number; name: string }[]}
                                value={field.value}
                                getResponseResult={(limit: number, pageNumber: number, searchText: string, signal: AbortSignal): Promise<ResponseResult> => {
                                    const tenantId = store.getState().userReducer.tenant.id;

                                    return getJourneyLabelsByTenantId(tenantId, limit, pageNumber, searchText, signal, true, labelInstance.type);
                                }}
                                onChange={(_e, newValue) => {
                                    field.onChange(newValue);
                                }}
                                filterSelectedOptions
                                onBlur={() => field.onBlur()}
                                getOptionLabel={(option) => GenericAutocompleteUtils.getOptionLabelCreateMode(option, option.name)}
                                renderOption={(propsItems, option) =>
                                    GenericAutocompleteUtils.renderOptionCreateMode(propsItems, option, option?.name, "create tag")
                                }
                                freeSolo
                                fetchWithOffset
                                disablePortal={false}
                            />
                        )}
                    />
                </Box>

                <Box>
                    <Controller
                        control={control}
                        name="disableSourceLabel"
                        render={({ field: { value, onChange, ...field } }) => (
                            <FormControlLabel
                                sx={{ marginBottom: "16px" }}
                                control={<Checkbox checked={value} onChange={onChange} {...field} />}
                                onChange={(_, checked) => onChange(checked)}
                                label={
                                    <FormattedMessage
                                        id="migrate-label-tag-modal.label.disable-source-label"
                                        defaultMessage="Disable source label ({labelName}) after migration is complete"
                                        values={{ labelName: labelInstance.name }}
                                    />
                                }
                            />
                        )}
                    />
                </Box>
                <Alert severity="warning">
                    <Typography variant="body2" color="warning">
                        <FormattedMessage
                            id="migrate-label-tag-modal.warning"
                            defaultMessage="This Action cannot be undone. All {count} instances will be moved permanently."
                            values={{ count: labelInstance.usageCount }}
                        />
                    </Typography>
                </Alert>
            </DialogContent>
            <Divider />
            <DialogActions>
                <Button
                    onClick={() => {
                        onClose();
                    }}
                >
                    <FormattedMessage id="common.button-label.cancel" defaultMessage="Cancel" />
                </Button>
                <Button onClick={handleSubmit(onSubmitForm)}>
                    <FormattedMessage id="migrate-label-tag-modal.label.migrate-label" defaultMessage="Migrate Label" />
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MigrateLabelModal;
